// src/pages/LegacyDetailPage.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactDOM from "react-dom";
import dayjs from "dayjs";
import { BASE_URL } from "../utils";
import { FiPaperclip, FiUsers } from "react-icons/fi";
import { FaPlus, FaMinus } from "react-icons/fa";

/* -------------------------
   Small helpers
-------------------------- */
const safeArr = (v) => (Array.isArray(v) ? v : []);
const toStr = (v) => (v == null ? "" : String(v));
const onlyDigits = (v) => toStr(v).replace(/\D+/g, ""); // ✅ phone: digits only

/* Portal modal used for HTML viewing */
function PortalModal({ open, title, children, onClose }) {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <div>
            <button onClick={onClose} className="px-2 py-1 border rounded text-sm">
              Close
            </button>
          </div>
        </div>
        <div className="raw-html-preview prose max-w-full text-sm">{children}</div>
      </div>
    </div>,
    document.body
  );
}

/* Formatting helper: MM/DD/YYYY */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function formatDateMDY(dateLike) {
  if (!dateLike && dateLike !== 0) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/* MetaRow now accepts ReactNode value so we can pass left-aligned nodes */
function MetaRow({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      {/* ✅ Title-like label (matches your Full Name look) */}
      <div className="text-base font-semibold text-[var(--fg)] leading-5">
        {label}
      </div>

      {/* ✅ Value slightly softer than label */}
      <div className="text-sm font-normal text-[var(--fg-muted)] break-words leading-5">
        {value || "—"}
      </div>
    </div>
  );
}

function Field({ label, value, editable, onChange = () => {}, textarea = false }) {
  return (
    <div className="mt-4">
      {label && (
        // ✅ EXACT same style as MetaRow label
        <label className="text-base font-semibold text-[var(--fg)] leading-5 block">
          {label}
        </label>
      )}

      {editable ? (
        textarea ? (
          <textarea
            className="field mt-2 w-full rounded-lg px-2 py-2 text-sm font-normal text-[var(--fg)]"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
          />
        ) : (
          <input
            className="field mt-2 w-full h-9 rounded-lg px-2 text-sm font-normal text-[var(--fg)]"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      ) : (
        // ✅ EXACT same style as MetaRow value
        <div className="mt-1 text-sm font-normal text-[var(--fg-muted)] break-words leading-5">
          {value || "—"}
        </div>
      )}
    </div>
  );
}




/* NoteItem component (view-only PDF in list) */
function NoteItem({ note, onEdit, onDelete, onUpdate, renderNoteContent }) {
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [editedText, setEditedText] = useState(note.text || "");

  useEffect(() => {
    setEditedText(note.text || "");
  }, [note.text]);

  const handleSaveInline = async () => {
    if (!onUpdate) return;
    await onUpdate(editedText);
    setIsEditingInline(false);
  };

  const buildPdfHrefLocal = (pdf_url) => {
    if (!pdf_url) return null;
    if (pdf_url.startsWith("http://") || pdf_url.startsWith("https://")) return pdf_url;
    const path = pdf_url.startsWith("/") ? pdf_url : `/${pdf_url}`;
    return `${BASE_URL}${path}`;
  };

  const pdfHref = note.pdf_url || note.pdfUrl ? buildPdfHrefLocal(note.pdf_url || note.pdfUrl) : null;

  return (
    <div className="text-sm mb-3 pl-2 border-l-4 border-gray-300">
      {isEditingInline ? (
        <>
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            className="w-full text-sm border px-2 py-1 rounded"
          />
          <div className="flex gap-2 mt-1">
            <button onClick={handleSaveInline} className="text-green-600 text-xs font-semibold">
              💾 Save
            </button>
            <button onClick={() => setIsEditingInline(false)} className="text-gray-500 text-xs">
              ❌ Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-gray-800 mb-2" dangerouslySetInnerHTML={renderNoteContent(note.text)} />

          {pdfHref ? (
            <div className="mb-2">
              <a
                href={pdfHref}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 underline inline-flex items-center gap-1"
                title="Open attached PDF"
              >
                <FiPaperclip size={14} /> View Attached PDF
              </a>
            </div>
          ) : null}

          <div className="text-xs text-gray-500 flex flex-col sm:flex-row sm:justify-between gap-2">
            <span className="block">{note.timestamp}</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onEdit && onEdit(note)}
                className="text-indigo-500 text-xs inline-flex items-center gap-1"
              >
                ✏️ Edit
              </button>
              <button onClick={() => onDelete && onDelete(note)} className="text-red-500 text-xs">
                🗑️ Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* Helper: buildCaseId similar to LegacyHome */
function buildCaseIdFromRecord(data) {
  if (!data) return "—";
  const b = data.property_borough ?? data.property_borough_name ?? "";
  const block = data.property_block ?? "";
  const lot = data.property_lot ?? "";
  if (![b, block, lot].some((x) => x !== null && x !== undefined && String(x).trim() !== "")) {
    return "—";
  }
  const parts = [b, block, lot].map((p) => (p === null || p === undefined ? "" : String(p).trim()));
  return `${parts[0] || ""}-${parts[1] || ""}-${parts[2] || ""}`
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

/* make match address look nicer */
function prettyMatchAddress(raw) {
  if (!raw) return "—";
  try {
    let s = String(raw);
    s = s.replace(/\b(house\s*no\.?|house|h\s*no|hno|street|st(?:reet)?|borough|boro|blk)\b\s*[:\-]?\s*/gi, "");
    s = s.replace(/[;|\/\\]+/g, ", ");
    s = s.replace(/\s*,\s*/g, ", ");
    s = s.replace(/,+/g, ",");
    s = s.replace(/,\s*,/g, ",");
    s = s.replace(/\s{2,}/g, " ");
    s = s.trim().replace(/^[,.-\s]+|[,.-\s]+$/g, "");
    s = s.replace(/\s{2,}/g, " ");
    return s || "—";
  } catch (e) {
    return String(raw);
  }
}

/* helpers used by editor/notes */
const escapeHtml = (s) => String(s || "");
const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, "").replace(/\u00A0/g, " ").trim();
};

const sanitizeForView = (html) => {
  if (!html) return "";
  let s = String(html);
  s = s.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
  s = s.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  try {
    s = DOMPurify.sanitize(s, { ADD_ATTR: ["target", "class", "style", "data-*"] });
  } catch (e) {}
  return s;
};

const sanitizeHtmlForView = (html) => {
  if (!html) return "";
  let s = html.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
  s = s.replace(/\son\w+="[^"]*"/gi, "");
  s = s.replace(/\son\w+='[^']*'/gi, "");
  try {
    s = DOMPurify.sanitize(s, { ADD_ATTR: ["target", "class", "style", "data-*"] });
  } catch (e) {}
  return s;
};

/* =========================
   Family history helpers
   ========================= */
function safeParseFamilyHistory(v) {
  try {
    if (!v) return { summary: "", relations: [] };
    if (typeof v === "object") {
      return {
        summary: typeof v.summary === "string" ? v.summary : "",
        relations: Array.isArray(v.relations) ? v.relations : [],
      };
    }
    const obj = JSON.parse(String(v));
    return {
      summary: typeof obj?.summary === "string" ? obj.summary : "",
      relations: Array.isArray(obj?.relations) ? obj.relations : [],
    };
  } catch {
    return { summary: "", relations: [] };
  }
}

function hasMeaningfulFamilyHistory(rec) {
  if (!rec) return false;
  const fh = rec.family_history;
  if (fh === null || fh === undefined) return false;

  if (typeof fh === "object") {
    const relations = Array.isArray(fh.relations) ? fh.relations : fh.relations == null ? [] : null;
    const summary =
      typeof fh.summary === "string"
        ? fh.summary.trim()
        : fh.summary == null
        ? ""
        : String(fh.summary).trim();
    if (Array.isArray(relations) && relations.length === 0 && summary === "") return false;
    if ((Array.isArray(relations) && relations.length > 0) || summary !== "") return true;
    return false;
  }

  if (typeof fh === "string") {
    const trimmed = fh.trim();
    if (trimmed === "") return false;
    const lower = trimmed.toLowerCase();
    if (lower === "na") return false;
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        const rels = Array.isArray(parsed?.relations) ? parsed.relations : [];
        const summ = (parsed?.summary ?? "").toString().trim();
        if (rels.length === 0 && summ === "") return false;
        return true;
      } catch (e) {}
    }
    return true;
  }

  return false;
}

/* =========================
   ✅ Family History Modal (LegacyHome-style)
   - ✅ Phone digits only
   - ✅ Address field added
   ========================= */
function FamilyHistoryModal({
  open,
  onClose,
  onSave,
  saving,
  fullName,
  summary,
  setSummary,
  members,
  setMembers,
}) {
  if (!open) return null;

  const addMember = () => {
    setMembers((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: `m_${Date.now()}`,
        name: "",
        relationship: "",
        contacts: [{ phone: "", address: "", email: "" }],
      },
    ]);
  };

  const removeMember = (memberIndex) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const next = arr.filter((_, i) => i !== memberIndex);
      return next.length ? next : [];
    });
  };

  const updateMemberField = (memberIndex, key, value) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => (i === memberIndex ? { ...m, [key]: value } : m));
    });
  };

  const addContactRow = (memberIndex) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => {
        if (i !== memberIndex) return m;
        const contacts = Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [];
        contacts.push({ phone: "", address: "", email: "" });
        return { ...m, contacts };
      });
    });
  };

  const removeContactRow = (memberIndex, contactIndex) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => {
        if (i !== memberIndex) return m;
        const contacts = Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [];
        const next = contacts.filter((_, ci) => ci !== contactIndex);
        return { ...m, contacts: next.length ? next : [{ phone: "", address: "", email: "" }] };
      });
    });
  };

const labelCls = "text-[11px] font-semibold text-slate-600 tracking-wide";
const inputCls =
  "w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300";
const thCls = "text-left px-3 py-2 text-[12px] font-semibold text-slate-600";
const tdCls = "px-3 py-2 align-top";
const btnBase =
  "inline-flex items-center justify-center rounded-md border border-slate-200 bg-white hover:bg-slate-50 " +
  "text-sm px-3 py-1.5";
const btnPrimary =
  "inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white " +
  "text-sm px-4 py-2 disabled:bg-slate-400 disabled:cursor-not-allowed";


  const updateContactField = (memberIndex, contactIndex, key, value) => {
    setMembers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((m, i) => {
        if (i !== memberIndex) return m;
        const contacts = Array.isArray(m.contacts)
          ? m.contacts.map((c) => ({ ...c }))
          : [{ phone: "", address: "", email: "" }];
        while (contacts.length <= contactIndex) contacts.push({ phone: "", address: "", email: "" });
        contacts[contactIndex][key] = value;
        return { ...m, contacts };
      });
    });
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99998] flex items-center justify-center bg-black bg-opacity-40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded shadow-lg w-full max-w-6xl p-4 max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold">Family History (edit)</h3>
            <div className="text-xs text-gray-500">
              Add / edit / remove relationships + contacts and Save
              {fullName ? ` • ${fullName}` : ""}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1 border rounded text-sm">
              Close
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className={`px-3 py-1 rounded text-sm text-white ${
                saving ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Summary</label>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            className="w-full border rounded px-2 py-2 text-sm"
            rows={3}
            placeholder="Summary..."
          />
        </div>

        <div className="mb-3">
          <button
            type="button"
            onClick={addMember}
            className="ui-btn"
          >
            + Add Family Member
          </button>
        </div>

        <div className="border rounded overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
              <tr>
                    {/* ✅ compressed */}
                    <th className={`${thCls} w-[180px]`}>Name</th>
                    <th className={`${thCls} w-[140px]`}>Relationship</th>
                    <th className={thCls}>Contacts (phone / address / email)</th>
                    <th className="px-3 py-2 text-[12px] font-semibold text-[var(--fg-muted)] text-center w-[90px]">
                      Remove
                    </th>
                  </tr>
            </thead>

            <tbody>
              {(members || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-3 text-center text-xs text-gray-500">
                    No family members yet. Click “Add Family Member”.
                  </td>
                </tr>
              ) : null}

              {(members || []).map((m, idx) => (
                <tr key={m.id || idx} className="border-t">
                  <td className="p-2 align-top">
                    <input
                      value={m.name || ""}
                      onChange={(e) => updateMemberField(idx, "name", e.target.value)}
                      className="ui-input w-full"
                      placeholder="Name"
                    />
                  </td>

                  <td className="p-2 align-top">
                    <input
                      value={m.relationship || ""}
                      onChange={(e) => updateMemberField(idx, "relationship", e.target.value)}
                      className="ui-input w-full"
                      placeholder="Relationship"
                    />
                  </td>

                  <td className="p-2 align-top">
                    <div className="border rounded p-2 bg-white">
                      <div className="space-y-2">
                        {(Array.isArray(m.contacts) ? m.contacts : [{ phone: "", address: "", email: "" }]).map(
                          (c, ci) => (
                            <div key={ci} className="grid grid-cols-[220px_1fr_1fr_90px] gap-2 items-center">
                              {/* ✅ Phone numbers only */}
                              <input
                                value={c.phone ?? ""}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={15}
                                onChange={(e) =>
                                  updateContactField(idx, ci, "phone", onlyDigits(e.target.value))
                                }
                                onKeyDown={(e) => {
                                  const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
                                  if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault();
                                }}
                                onPaste={(e) => {
                                  e.preventDefault();
                                  const pasted = e.clipboardData.getData("text");
                                  updateContactField(idx, ci, "phone", onlyDigits(pasted));
                                }}
                                className="ui-input w-full font-mono"
                                placeholder="Phone"
                              />

                              {/* ✅ Address */}
                              <input
                                value={c.address ?? ""}
                                onChange={(e) => updateContactField(idx, ci, "address", e.target.value)}
                                className="ui-input w-full"
                                placeholder="Address"
                              />

                              <input
                                value={c.email ?? ""}
                                onChange={(e) => updateContactField(idx, ci, "email", e.target.value)}
                                className="ui-input w-full"
                                placeholder="Email"
                              />

                              <button
                                type="button"
                                onClick={() => removeContactRow(idx, ci)}
                                className="ui-btn text-xs text-red-600"
                                title="Remove contact row"
                              >
                                ×
                              </button>
                            </div>
                          )
                        )}

                        <button
                          type="button"
                          onClick={() => addContactRow(idx)}
                          className="ui-btn text-xs inline-flex items-center gap-2"
                        >
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-blue-600 text-white text-[10px]">
                            +
                          </span>
                          Add contact row
                        </button>
                      </div>
                    </div>
                  </td>

                  <td className="p-2 align-top text-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm("Remove this family member?")) return;
                        removeMember(idx);
                      }}
                      className="ui-btn text-xs text-red-600"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="ui-btn">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className={`ui-btn ui-btn-primary ${saving ? "opacity-60" : ""}`}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* =========================
   Main page
   ========================= */
export default function LegacyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // surrogate match status
  const [surrogateMatches, setSurrogateMatches] = useState(null);
  const [isHighPriority, setIsHighPriority] = useState(false);

  // acris parties
  const [familyMembers, setFamilyMembers] = useState([]);
  const [membersDirty, setMembersDirty] = useState(false);

  // ✅ Family History (Manual) modal state (LegacyHome-style)
  const [fhModalOpen, setFhModalOpen] = useState(false);
  const [fhSaving, setFhSaving] = useState(false);
  const [fhSummary, setFhSummary] = useState("");
  const [fhMembers, setFhMembers] = useState([]); // [{id,name,relationship,contacts:[{phone,address,email}]}]

  // notes/editor state
  const [notes, setNotes] = useState([]);
  const editorRef = useRef(null);
  const [appliedRawHtml, setAppliedRawHtml] = useState("");
  const [showPasteHtml, setShowPasteHtml] = useState(false);
  const [pasteHtmlText, setPasteHtmlText] = useState("");

  // html viewer modal
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");

  // edit existing note
  const [currentNoteId, setCurrentNoteId] = useState(null);

  // PDF upload
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // saving statuses
  const [saveLoading, setSaveLoading] = useState(false);

  // Status UI states
  const [activeStatus, setActiveStatus] = useState("Approved");
  const subStatusOptions = ["NA", "Research", "Follow Up", "Not Selling", "Evernote", "Selling", "fresh lead"];
  const [subStatus, setSubStatus] = useState("NA");
  const [subStatusManuallySet, setSubStatusManuallySet] = useState(false);
  const [label, setLabel] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");

  // edit fields mode
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState({});
  const [savingFields, setSavingFields] = useState(false);

  // Source link extraction
  const guessSourceUrl = (rec) => {
    if (!rec) return null;
    return rec.source_url || rec.source || rec.origin_url || rec.found_at || rec.found_from || rec.url || rec.info_url || null;
  };

  /* fetch record + surrogate + notes */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/legacy/${id}`);
      const rec = res?.data || res?.data?.data || res;
      setData(rec || {});

      // parse parties and normalize into contacts array + relationship
      let members = [];
      try {
        const raw = rec?.acris_parties_json;
        if (raw) {
          const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (Array.isArray(parsed)) {
            members = parsed;
          } else if (parsed && typeof parsed === "object") {
            if (Array.isArray(parsed.parties)) {
              members = parsed.parties;
            } else {
              // convert key/value map -> array of parties
              members = Object.entries(parsed).map(([role, name]) => ({
                role,
                relationship: role,
                name: name ?? "",
                party_name: name ?? "",
              }));
            }
          }
        }
      } catch (err) {
        console.warn("parse acris_parties_json failed", err);
        members = [];
      }
      if (!Array.isArray(members)) members = [];

      const normalizedMembers = (members || []).map((m, i) => {
        const primaryPhone = m.phone || m.telephone || null;
        const primaryEmail = m.email || null;

        const contacts = [];
        if (Array.isArray(m.contacts) && m.contacts.length) {
          for (const c of m.contacts) contacts.push({ phone: c?.phone ?? "", email: c?.email ?? "" });
        } else if (Array.isArray(m.phones) || Array.isArray(m.emails)) {
          const phones = Array.isArray(m.phones) ? m.phones : primaryPhone ? [primaryPhone] : [];
          const emails = Array.isArray(m.emails) ? m.emails : primaryEmail ? [primaryEmail] : [];
          const max = Math.max(phones.length, emails.length);
          for (let k = 0; k < Math.max(max, 1); k++) contacts.push({ phone: phones[k] ?? "", email: emails[k] ?? "" });
        } else {
          contacts.push({ phone: primaryPhone ?? "", email: primaryEmail ?? "" });
        }

        return {
          __idx: i,
          name: m.name || m.party_name || "",
          address_1: m.address_1 || m.address || "",
          city: m.city || "",
          state: m.state || "",
          zip: m.zip || "",
          relationship: m.relationship || m.role || "",
          contacts: contacts.length ? contacts : [{ phone: "", email: "" }],
          raw: m,
        };
      });

      setFamilyMembers(normalizedMembers);

      // surrogate matches (left empty as before)
      let surMatches = [];
      setSurrogateMatches(surMatches);
      setIsHighPriority(!(Array.isArray(surMatches) && surMatches.length > 0));

      // notes - normalized
      try {
        const notesRes = await axios.get(`${BASE_URL}/api/legacy/${id}/notes`);
        const fetched = notesRes?.data?.data || notesRes?.data || notesRes;
        let arr = Array.isArray(fetched) ? fetched : fetched.notes || [];
        if (!Array.isArray(arr)) arr = [];

        const normalized = arr.map((n) => {
          const createdRaw = n.created_at || n.createdAt || n.created || n.timestamp;
          const timestamp = createdRaw
            ? (() => {
                const d = dayjs(createdRaw);
                if (d && d.isValid && d.isValid()) return d.format("MM/DD/YYYY, h:mm A");
                try {
                  const parsed = new Date(createdRaw);
                  if (!Number.isNaN(parsed.getTime())) {
                    const mm = pad2(parsed.getMonth() + 1);
                    const dd = pad2(parsed.getDate());
                    const yyyy = parsed.getFullYear();
                    const hours = parsed.getHours();
                    const mins = pad2(parsed.getMinutes());
                    const ampm = hours >= 12 ? "PM" : "AM";
                    const h12 = ((hours + 11) % 12) + 1;
                    return `${mm}/${dd}/${yyyy}, ${h12}:${mins} ${ampm}`;
                  }
                } catch (e) {}
                return String(createdRaw || "");
              })()
            : "";

          return {
            id: n.id || n.noteId || n.note_id || null,
            text: n.note || n.text || n.html || n.note_html || "",
            plain_text: n.plain_text || n.plainText || n.plain || "",
            pdf_url: n.pdf_url || n.pdfUrl || n.url || null,
            timestamp,
            status: n.status || n.state || null,
            sub_status: n.sub_status || n.subStatus || null,
            label: n.label || null,
            followUp_date: n.followUp_date || n.followUpDate || null,
            raw: n,
          };
        });

        setNotes(normalized);

        if (normalized.length > 0) {
          const latest = normalized[0];
          const derivedStatus = latest.status || rec.latest_status || rec.status || "Approved";
          const derivedSub = latest.sub_status || rec.latest_sub_status || rec.sub_status || "NA";
          const derivedLabel = latest.label || rec.latest_label || rec.label || "";

          setActiveStatus(derivedStatus);
          setSubStatus(derivedSub);
          setLabel(derivedLabel);
          setSubStatusManuallySet(false);

          if (String(derivedSub).toLowerCase() === "follow up" && latest.followUp_date) {
            try {
              const fd = new Date(latest.followUp_date);
              if (!Number.isNaN(fd.getTime())) setFollowUpDate(fd.toISOString().split("T")[0]);
            } catch (e) {}
          } else {
            setFollowUpDate(latest.followUp_date || "");
          }
          setShowCalendar(false);
        } else {
          const recStatus = rec.latest_status || rec.status || "Approved";
          const recSub = rec.latest_sub_status || rec.sub_status || "NA";
          const recLabel = rec.latest_label || rec.label || "";
          setActiveStatus(recStatus);
          setSubStatus(recSub);
          setLabel(recLabel);
          setSubStatusManuallySet(false);
          setShowCalendar(false);
          setFollowUpDate(rec.followUp_date ? String(rec.followUp_date) : "");
        }
      } catch (err) {
        console.warn("notes fetch failed", err);
        setNotes([]);
      }
    } catch (err) {
      console.error("fetchAll error", err);
      toast.error("Failed to load legacy detail.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* show html viewer */
  const showHtmlContent = useCallback((html, title = "HTML View") => {
    setHtmlModalContent(sanitizeForView(html));
    setHtmlModalOpen(true);
  }, []);

  /* ✅ Open Family History (Manual) modal with LegacyHome-style data (NOW supports address + phone digits only) */
  const openFamilyHistoryModal = useCallback(() => {
    const parsed = safeParseFamilyHistory(data?.family_history);
    setFhSummary((parsed?.summary ?? "").toString());

    const rels = Array.isArray(parsed?.relations) ? parsed.relations : [];
    const members = rels.map((r, idx) => {
      const phonesRaw = Array.isArray(r?.phones) ? r.phones : r?.phone ? [r.phone] : [];
      const phones = phonesRaw.map((p) => onlyDigits(p)).filter((p) => p !== "");

      const emails = Array.isArray(r?.emails) ? r.emails : r?.email ? [r.email] : [];
      const addresses = Array.isArray(r?.addresses) ? r.addresses : r?.address ? [r.address] : [];

      const max = Math.max(phones.length, emails.length, addresses.length, 1);

      const contacts = [];
      for (let i = 0; i < max; i++) {
        contacts.push({
          phone: phones[i] ?? "",
          address: (addresses[i] ?? "").toString(),
          email: (emails[i] ?? "").toString(),
        });
      }

      return {
        id: r?.id || `fh_${idx}_${Date.now()}`,
        name: (r?.name ?? "").toString(),
        relationship: (r?.relationship ?? "").toString(),
        contacts,
      };
    });

    setFhMembers(members);
    setFhModalOpen(true);
  }, [data]);

  /* ✅ Save Family History (Manual) to backend (NOW supports address + phone digits only) */
  const saveFamilyHistoryManual = useCallback(async () => {
    if (!id) return;
    setFhSaving(true);

    try {
      const relations = (fhMembers || [])
        .map((m) => {
          const name = (m?.name ?? "").toString().trim();
          const relationship = (m?.relationship ?? "").toString().trim();

          const contacts = Array.isArray(m?.contacts) ? m.contacts : [];

          const phones = contacts
            .map((c) => onlyDigits(c?.phone ?? ""))
            .filter(Boolean);

          const emails = contacts
            .map((c) => (c?.email ?? "").toString().trim())
            .filter(Boolean);

          const addresses = contacts
            .map((c) => (c?.address ?? "").toString().trim())
            .filter(Boolean);

          // drop empty member
          if (!name && !relationship && phones.length === 0 && emails.length === 0 && addresses.length === 0) return null;

          return {
            name: name || null,
            relationship: relationship || null,

            phones,
            emails,
            addresses,

            phone: phones[0] || null,
            email: emails[0] || null,
            address: addresses[0] || null,
          };
        })
        .filter(Boolean);

      const payload = {
        family_history: {
          summary: (fhSummary ?? "").toString(),
          relations,
        },
      };

      await axios.put(`${BASE_URL}/api/legacy/${id}/family_history`, payload);
      toast.success("Family History saved.");
      setFhModalOpen(false);
      await fetchAll();
    } catch (err) {
      console.error("saveFamilyHistoryManual error", err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || "Failed to save family history";
      toast.error(String(msg));
    } finally {
      setFhSaving(false);
    }
  }, [id, fhSummary, fhMembers, fetchAll]);

  /* paste handling */
  const handleEditorPaste = (e) => {
    if (!e.clipboardData) return;
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (html) insertHtmlAtCaret(html);
    else if (text) insertHtmlAtCaret(text.replace(/\r\n|\r|\n/g, "<br>"));
  };

  function insertHtmlAtCaret(html) {
    try {
      document.execCommand("insertHTML", false, html);
      return;
    } catch (err) {}
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      if (editorRef.current) editorRef.current.insertAdjacentHTML("beforeend", html);
      return;
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const el = document.createElement("div");
    el.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node;
    while ((node = el.firstChild)) frag.appendChild(node);
    range.insertNode(frag);
    if (frag.lastChild) {
      range.setStartAfter(frag.lastChild);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  /* apply pasted raw HTML (preserve styles) */
  const applyPastedHtml = () => {
    try {
      if (!pasteHtmlText || !pasteHtmlText.trim()) {
        toast.error("No HTML provided.");
        return;
      }
      const sanitized = DOMPurify.sanitize(pasteHtmlText, {
        ALLOWED_ATTR: ["style", "class", "src", "href", "alt", "title", "target", "data-*"],
      });
      if (!sanitized || sanitized.trim() === "") {
        toast.error("Sanitization removed all content — paste different HTML.");
        return;
      }
      setAppliedRawHtml(sanitized);
      if (editorRef.current) editorRef.current.innerHTML = sanitized;
      setShowPasteHtml(false);
      setPasteHtmlText("");
      setCurrentNoteId(null);
      toast.success("Raw HTML applied and preserved for preview. Click Save to persist.");
    } catch (err) {
      console.error("applyPastedHtml error", err);
      toast.error("Failed to apply pasted HTML.");
    }
  };

  /* PDF upload */
  const handlePdfUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please upload a PDF file.");
    const formData = new FormData();
    formData.append("pdf", file);
    try {
      setUploadingPdf(true);
      const uploadRes = await axios.post(`${BASE_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedUrl =
        uploadRes?.data?.url || uploadRes?.data?.fileURL || uploadRes?.data?.fileUrl || uploadRes?.data?.url;
      if (!uploadedUrl) throw new Error("Upload returned no URL.");
      setUploadedPdfUrl(uploadedUrl);
      toast.success("📄 PDF uploaded successfully and ready to attach to note.");
    } catch (err) {
      console.error("PDF upload error", err);
      toast.error("Failed to upload PDF.");
    } finally {
      setUploadingPdf(false);
    }
  };

  /* inline update helper */
  const updateNoteInline = async (note, newText) => {
    const noteId = note.id || note.noteId;
    if (!noteId) throw new Error("Missing note id");
    try {
      await axios.put(`${BASE_URL}/api/legacy/${id}/notes/${noteId}`, {
        note: newText,
        plain_text: stripHtml(newText),
        status: note.status || activeStatus,
        sub_status: note.sub_status || subStatus,
        label: note.label || label,
      });
      toast.success("Note updated.");
      await fetchAll();
    } catch (err) {
      console.error("inline update error", err);
      toast.error("Failed to update note");
      throw err;
    }
  };

  /* delete note */
  const deleteNote = async (note) => {
    const nid = note.id || note.noteId;
    if (!nid) return;
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${BASE_URL}/api/legacy/note/${nid}`).catch(async (err) => {
        try {
          await axios.delete(`${BASE_URL}/api/legacy/${id}/notes/${nid}`);
        } catch (e) {
          throw err;
        }
      });
      toast.success("Note deleted");
      await fetchAll();
    } catch (err) {
      console.error("delete note error", err);
      toast.error("Failed to delete note");
    }
  };

  /* load note into editor for editing (main editor) */
  const loadNoteIntoEditorForEdit = (note) => {
    if (!note) return;
    setCurrentNoteId(note.id || note.noteId || null);
    setAppliedRawHtml("");
    if (editorRef.current) editorRef.current.innerHTML = note.text || note.note || "";
    setUploadedPdfUrl(note.pdf_url || note.pdfUrl || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Loaded note into editor for editing.");
  };

  /* onSubStatusChange */
  const onSubStatusChange = (selected) => {
    setSubStatus(selected);
    setSubStatusManuallySet(true);
    setShowCalendar(String(selected) === "Follow Up");
    if (selected !== "Follow Up") setFollowUpDate("");
  };

  /* Follow-up date insertion */
  const handleFollowUpPick = (date) => {
    if (!date) return;
    const formattedDisplay = date.toLocaleDateString("en-US");
    const isoDate = date.toISOString().split("T")[0];
    setFollowUpDate(isoDate);
    const followHtml = `<p><strong>Follow-up on: ${formattedDisplay}</strong></p>`;
    const currentHtml =
      appliedRawHtml && appliedRawHtml.trim()
        ? appliedRawHtml
        : (editorRef.current ? editorRef.current.innerHTML : "") || "";
    const combined = followHtml + (currentHtml || "");
    if (editorRef.current) editorRef.current.innerHTML = combined;
    setAppliedRawHtml("");
    setShowCalendar(false);
  };

  /* Save Status — unified endpoint */
  const handleSaveStatus = async () => {
    const editorHtml = editorRef.current ? editorRef.current.innerHTML : "";
    const htmlToSave = appliedRawHtml && appliedRawHtml.trim() ? appliedRawHtml.trim() : (editorHtml || "").trim();
    const plain = stripHtml(htmlToSave);

    if (activeStatus === "Rejected" && !plain.trim()) {
      return toast.error("⚠️ Please enter a note before saving a Rejected status.");
    }

    if (activeStatus !== "Rejected" && !plain.trim() && !uploadedPdfUrl) {
      return toast.error("⚠️ Please enter a note or attach a PDF before saving status.");
    }

    if (activeStatus === "Approved") {
      if (!label || label === "") {
        return toast.error("⚠️ Please select a Label before saving.");
      }
    }

    if (subStatus === "Follow Up" && subStatusManuallySet) {
      const match = plain.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/);
      if (!match && !followUpDate) {
        return toast.error("⚠️ Please include a follow-up date in MM/DD/YYYY format in the note or pick the follow-up calendar.");
      }
    }

    setSaveLoading(true);
    try {
      const noteIdForEndpoint = currentNoteId ? currentNoteId : "new";
      const payload = {
        status: activeStatus,
        note: htmlToSave || null,
        plain_text: stripHtml(htmlToSave) || null,
        sub_status: subStatus || null,
        label: label || null,
        followUp_date: followUpDate || null,
        pdf_url: uploadedPdfUrl || null,
        created_by: null,
        updated_by: null,
      };

      const resp = await axios.put(`${BASE_URL}/api/legacy/${id}/notes/${noteIdForEndpoint}`, payload);
      toast.success(resp?.data?.message || "Status and note saved.");

      setAppliedRawHtml("");
      if (editorRef.current) editorRef.current.innerHTML = "";
      setCurrentNoteId(null);
      setUploadedPdfUrl(null);

      await fetchAll();
    } catch (err) {
      console.error("Error saving status via unified endpoint:", err);
      const msg = err?.response?.data?.message || "❌ Failed to save status.";
      toast.error(msg);
    } finally {
      setSaveLoading(false);
    }
  };

  /* Edit fields behavior */
  const handleFieldChange = (field, value) => {
    setData((p) => ({ ...(p || {}), [field]: value }));
    setDirty((d) => ({ ...(d || {}), [field]: true }));
  };

  const saveFields = async () => {
    if (!data?.id && !data?.legacy_id && !id) {
      toast.error("Missing legacy id to save.");
      return;
    }
    const rowId = data.id || data.legacy_id || id;

    const editableMap = {
      full_name: data.full_name,
      first_name: data.first_name,
      last_name: data.last_name,
      family_history: data.family_history,
      family_history_html: data.family_history_html,
      acris_document_id: data.acris_document_id,
      acris_document_date: data.acris_document_date,
    };

    const updates = Object.keys(dirty)
      .map((k) => {
        if (k === "decedent_real_property_address_only") {
          return { field: "decedent_real_property_description", value: data.decedent_real_property_address_only ?? "" };
        }
        if (!(k in editableMap)) {
          if (k in data) return { field: k, value: data[k] ?? "" };
          return null;
        }
        return { field: k, value: editableMap[k] ?? "" };
      })
      .filter(Boolean);

    if (updates.length === 0) {
      toast.info("No changes to save.");
      setEditMode(false);
      setDirty({});
      return;
    }

    setSavingFields(true);
    try {
      await axios.put(`${BASE_URL}/api/legacy/${rowId}/fields`, { updates });
      toast.success("Changes saved.");
      setEditMode(false);
      setDirty({});
      await fetchAll();
    } catch (err) {
      console.error("Failed to save legacy fields:", err);
      const msg = err?.response?.data?.error || "Some fields failed to save.";
      toast.error(msg);
    } finally {
      setSavingFields(false);
    }
  };

  /* ---------- Acris parties contact helpers ---------- */
  const addContactForMember = (memberIndex) => {
    setFamilyMembers((prev) => {
      const copy = prev.map((m) => ({
        ...m,
        contacts: Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [{ phone: "", email: "" }],
      }));
      const member = copy[memberIndex];
      if (!member) return prev;
      member.contacts = member.contacts || [{ phone: "", email: "" }];
      member.contacts.push({ phone: "", email: "" });
      setMembersDirty(true);
      return copy;
    });
  };

  const removeContactForMember = (memberIndex, contactIndex) => {
    setFamilyMembers((prev) => {
      const copy = prev.map((m) => ({
        ...m,
        contacts: Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [{ phone: "", email: "" }],
      }));
      const member = copy[memberIndex];
      if (!member) return prev;
      member.contacts = member.contacts || [{ phone: "", email: "" }];
      if (member.contacts.length <= 1) {
        member.contacts = [{ phone: "", email: "" }];
      } else {
        member.contacts.splice(contactIndex, 1);
      }
      setMembersDirty(true);
      return copy;
    });
  };

  const updateContactField = (memberIndex, contactIndex, key, value) => {
    setFamilyMembers((prev) => {
      const copy = prev.map((m) => ({
        ...m,
        contacts: Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [{ phone: "", email: "" }],
      }));
      const member = copy[memberIndex];
      if (!member) return prev;
      member.contacts = member.contacts || [{ phone: "", email: "" }];
      while (member.contacts.length <= contactIndex) member.contacts.push({ phone: "", email: "" });
      member.contacts[contactIndex][key] = value;
      setMembersDirty(true);
      return copy;
    });
  };

  const updateMemberField = (memberIndex, key, value) => {
    setFamilyMembers((prev) => {
      const copy = prev.map((m) => ({
        ...m,
        contacts: Array.isArray(m.contacts) ? m.contacts.map((c) => ({ ...c })) : [{ phone: "", email: "" }],
      }));
      if (!copy[memberIndex]) return prev;
      copy[memberIndex][key] = value;
      setMembersDirty(true);
      return copy;
    });
  };

  const saveFamilyMembers = async () => {
    const payload = familyMembers.map((m) => {
      const contacts = Array.isArray(m.contacts) ? m.contacts : [{ phone: m.phone || "", email: m.email || "" }];
      const phones = [];
      const emails = [];
      for (const c of contacts) {
        const ph = c && c.phone ? String(c.phone).trim() : null;
        const em = c && c.email ? String(c.email).trim() : null;
        if (ph) phones.push(ph);
        if (em) emails.push(em);
      }
      return {
        name: m.name,
        address_1: m.address_1,
        city: m.city,
        state: m.state,
        zip: m.zip,
        relationship: m.relationship || null,
        phone: phones.length > 0 ? phones[0] : m.phone || null,
        email: emails.length > 0 ? emails[0] : m.email || null,
        phones,
        emails,
        raw: m.raw || null,
      };
    });

    try {
      const res = await axios.put(`${BASE_URL}/api/legacy/${id}/acris_parties`, { parties: payload });
      toast.success(res?.data?.message || "Acris parties updated");
      setMembersDirty(false);

      // update main contact summary (non-fatal)
      try {
        const first = payload[0] || null;
        if (first) {
          const contact_phone = first.phone || null;
          const contact_email = first.email || null;
          if (contact_phone || contact_email) {
            await axios.put(`${BASE_URL}/api/legacy/${id}/contact`, { contact_phone, contact_email });
          }
        }
      } catch (err) {
        console.warn("update contact summary failed", err);
      }

      await fetchAll();
    } catch (err) {
      console.error("saveFamilyMembers error", err);
      toast.error("Failed to save Acris parties");
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;
  if (!data) return <div className="p-6 text-red-600">No legacy data found</div>;

  const matchPartyName = data.matched_party_name || data.acris_match_party_name || null;
  const matchAddressRaw = (() => {
    if (!data) return "—";
    if (data.property_full_address) return data.property_full_address;
    try {
      const arr = data.acris_addresses_json;
      if (Array.isArray(arr) && arr.length) {
        const a = arr[0];
        if (a.address) return a.address;
        if (a.street_number || a.street_name) {
          return `${a.street_number ? a.street_number + " " : ""}${a.street_name || ""}${a.unit ? " " + a.unit : ""}`.trim();
        }
      }
    } catch (e) {}
    if (data.obituary_city) return data.obituary_city;
    return "—";
  })();
  const matchAddress = prettyMatchAddress(matchAddressRaw);
  const caseIdString = buildCaseIdFromRecord(data);
  const sourceLink = guessSourceUrl(data);

  const fhParsed = safeParseFamilyHistory(data?.family_history);
  const fhSummaryParsed = (fhParsed?.summary ?? "").toString();
  const fhRelationsParsed = Array.isArray(fhParsed?.relations) ? fhParsed.relations : [];

  return (
    <div
      className="p-6 bg-[var(--bg)] text-[var(--fg)]"
      style={{ maxHeight: "calc(100vh - 100px)", overflowY: "auto", paddingBottom: 48 }}
    >
      <style>{`
        .raw-html-preview { box-sizing: border-box; max-width: 100%; overflow-x: auto; overflow-y: auto; word-wrap: break-word; }
        .raw-html-preview * { box-sizing: border-box; max-width: 100% !important; }
        .raw-html-preview img, .raw-html-preview video, .raw-html-preview iframe { max-width: 100% !important; height: auto !important; display:block; }
        .raw-html-preview table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse; }
        .raw-html-preview td, .raw-html-preview th { word-break: break-word !important; white-space: normal !important; }
        .raw-html-preview pre { white-space: pre-wrap !important; word-break: break-word !important; max-width: 100% !important; overflow-x: auto; }
      `}</style>

      <div className="filter-panel mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="page-kicker">Legacy</div>
            <h1 className="page-title flex items-center gap-3">
              Legacy Detail
              <span className={`badge-pill ${isHighPriority ? "badge-rose" : "badge-green"}`}>
                {isHighPriority ? "HIGH PRIORITY (not in Surrogates)" : "Matched in Surrogates"}
              </span>
            </h1>
            <div className="page-subtitle">{data.full_name || "—"}</div>
          </div>

          <div className="flex items-start gap-3">
            <div className="text-sm text-left mr-2">
              <div className="text-xs text-[var(--fg-muted)]">Case id</div>
              <div>
                {caseIdString && caseIdString !== "—" ? (
                  <a
                    href={data && data.id ? `/legacy/${data.id}` : "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline font-medium"
                  >
                    {caseIdString}
                  </a>
                ) : (
                  <span className="text-[var(--fg-muted)]">—</span>
                )}
              </div>
              {sourceLink ? (
                <div className="text-xs mt-1 text-[var(--fg-muted)]">
                  <a
                    href={sourceLink.startsWith("http") ? sourceLink : `${BASE_URL}${sourceLink}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-indigo-600 underline"
                  >
                    View source
                  </a>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <button onClick={() => navigate(-1)} className="ui-btn">
                  Back
                </button>

                {!editMode ? (
                  <button
                    onClick={() => {
                      if (!window.confirm("Enable editing for this legacy record?")) return;
                      setEditMode(true);
                      setDirty({});
                      toast.info("Editing enabled. Make your changes and click Save Changes.");
                    }}
                    className="ui-btn"
                  >
                    Edit fields
                  </button>
                ) : (
                  <>
                    <button
                      onClick={saveFields}
                      disabled={savingFields}
                      className="ui-btn ui-btn-primary disabled:opacity-60"
                    >
                      {savingFields ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setDirty({});
                        fetchAll();
                      }}
                      className="ui-btn"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* top meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="surface-card rounded-2xl p-4">
          <MetaRow label="Full Name" value={data.full_name} />
          <Field
            label="First Name"
            value={data.first_name || ""}
            editable={editMode}
            onChange={(v) => handleFieldChange("first_name", v)}
          />
          <Field
            label="Last Name"
            value={data.last_name || ""}
            editable={editMode}
            onChange={(v) => handleFieldChange("last_name", v)}
          />
        </div>

        <div className="surface-card rounded-2xl p-4">
          <MetaRow label="Death Date" value={formatDateMDY(data.obituary_published_date)} />
          <MetaRow label="Created" value={formatDateMDY(data.created_at)} />
        </div>

        <div className="surface-card rounded-2xl p-4">
            <h3 className="section-title mb-2">Match / Compare</h3>

            <div className="text-sm mb-2">
  <span className="font-semibold text-[var(--fg-muted)]">Surrogate matches count:</span>{" "}
  <span className="font-normal text-[var(--fg)]">
    {Array.isArray(surrogateMatches) ? surrogateMatches.length : "—"}
  </span>
</div>


            <div className="space-y-2">
              {Array.isArray(surrogateMatches) && surrogateMatches.length > 0 ? (
                surrogateMatches.slice(0, 5).map((s, i) => (
                  <div key={i} className="p-2 bg-white rounded border text-sm">
                    <div className="font-medium">
                      {s.decedent_name || s.full_name || s.name || "—"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {s.court || s.petition_court_type || ""} — {s.case_id || s.id || ""}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-red-600">
                  No surrogate decedent matches found for this name.
                </div>
              )}
            </div>

            {(() => {
              // --- helpers (kept inside block so you don't need to add more functions elsewhere) ---
              const BOROUGH_MAP = {
                "1": "Manhattan",
                "2": "Bronx",
                "3": "Brooklyn",
                "4": "Queens",
                "5": "Staten Island",
              };

              const clean = (v) => (v == null ? "" : String(v).trim());

              const parseAddresses = () => {
                try {
                  const raw = data?.acris_addresses_json;
                  if (Array.isArray(raw)) return raw;
                  if (typeof raw === "string" && raw.trim()) {
                    const parsed = JSON.parse(raw);
                    return Array.isArray(parsed) ? parsed : [];
                  }
                } catch {}
                return [];
              };

              // NYC ACRIS "Parcel Identifier" search link (borough/block/lot)
              // This URL pattern is commonly used; if your environment uses a different ACRIS host, swap it here.
              const buildAcrisParcelUrl = ({ borough, block, lot }) => {
                const b = clean(borough);
                const bl = clean(block);
                const lt = clean(lot);
                if (!b || !bl || !lt) return null;

                // ✅ use your reference URL
                const base = "https://a836-acris.nyc.gov/CP/LookUp/Index";

                // ✅ pass borough/block/lot in querystring (so user lands pre-filled)
                const qs = new URLSearchParams({
                  borough: b,
                  block: bl,
                  lot: lt,
                }).toString();

                return `${base}?${qs}`;
              };


              const buildMapsUrl = (addrObj) => {
                const addr = clean(addrObj?.address);
                const zip = clean(addrObj?.zip);
                const boroughCode = clean(addrObj?.borough);
                const boroughName = BOROUGH_MAP[boroughCode] || boroughCode || "";
                const q = [addr, boroughName && `${boroughName}, NY`, zip].filter(Boolean).join(" ");
                if (!q) return null;
                return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
              };

              const addresses = parseAddresses();

              // Normalize + enrich
              const normalized = addresses.map((a, idx) => {
                const boroughCode = clean(a?.borough);
                const boroughName = BOROUGH_MAP[boroughCode] || boroughCode || "—";
                const block = clean(a?.block);
                const lot = clean(a?.lot);
                const address = clean(a?.address);
                const zip = clean(a?.zip);

                const acrisUrl = buildAcrisParcelUrl({ borough: boroughCode, block, lot });
                const mapsUrl = buildMapsUrl({ borough: boroughCode, address, zip });

                return {
                  key: `${idx}_${boroughCode}_${block}_${lot}_${address}`,
                  boroughCode,
                  boroughName,
                  block,
                  lot,
                  address,
                  zip,
                  acrisUrl,
                  mapsUrl,
                };
              });

              const first = normalized[0] || null;
              const hasAny = first && (first.boroughCode || first.block || first.lot || first.address);

              return (
                <div className="mt-3 text-sm">
                  <MetaRow label="Matched Party" value={matchPartyName} />
                  <MetaRow label="Matched Address" value={matchAddress} />

                  {/* ✅ Borough/Block/Lot (formatted exactly as you asked) */}
                  <div className="text-sm text-gray-800 mt-1">
                    {hasAny ? (
                      <>
                        <strong>Borough :</strong> {first.boroughName}{" "}
                        <strong>Block :</strong> {first.block || "—"}{" "}
                        <strong>Lot :</strong> {first.lot || "—"}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>

                  {/* ✅ Quick links */}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {first?.acrisUrl ? (
                      <a
                        href={first.acrisUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ui-btn text-xs text-blue-600 underline"
                        title="Open NYC ACRIS parcel search (borough/block/lot)"
                      >
                        🔗 ACRIS Block/Lot Search
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">
                        🔗 ACRIS Block/Lot Search (need borough+block+lot)
                      </span>
                    )}

                    {first?.mapsUrl ? (
                      <a
                        href={first.mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ui-btn text-xs text-indigo-600 underline"
                        title="Open first address in Google Maps"
                      >
                        🗺️ Open in Google Maps
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">🗺️ Google Maps (no address)</span>
                    )}
                  </div>

                  {/* ✅ Show multiple addresses */}
                  {/* <div className="mt-3 border rounded bg-white overflow-auto">
                    <div className="px-3 py-2 border-b bg-[var(--surface-muted)] text-xs font-semibold text-[var(--fg-muted)]">
                      ACRIS Addresses ({normalized.length || 0})
                    </div>

                    {normalized.length === 0 ? (
                      <div className="p-3 text-xs text-gray-500">No addresses found in acris_addresses_json.</div>
                    ) : (
                      <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
                          <tr>
                            <th className="p-2 text-left w-[140px]">Borough</th>
                            <th className="p-2 text-left w-[120px]">Block</th>
                            <th className="p-2 text-left w-[120px]">Lot</th>
                            <th className="p-2 text-left">Address</th>
                            <th className="p-2 text-left w-[120px]">Zip</th>
                            <th className="p-2 text-left w-[220px]">Links</th>
                          </tr>
                        </thead>
                        <tbody>
                          {normalized.map((a) => (
                            <tr key={a.key} className="border-t">
                              <td className="p-2">{a.boroughName || "—"}</td>
                              <td className="p-2">{a.block || "—"}</td>
                              <td className="p-2">{a.lot || "—"}</td>
                              <td className="p-2">{a.address || "—"}</td>
                              <td className="p-2">{a.zip || "—"}</td>
                              <td className="p-2">
                                <div className="flex flex-wrap gap-2">
                                  {a.acrisUrl ? (
                                    <a
                                      href={a.acrisUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs text-blue-600 underline"
                                      title="Open NYC ACRIS parcel search (borough/block/lot)"
                                    >
                                      ACRIS
                                    </a>
                                  ) : (
                                    <span className="text-xs text-gray-400" title="Need borough+block+lot">
                                      ACRIS
                                    </span>
                                  )}

                                  {a.mapsUrl ? (
                                    <a
                                      href={a.mapsUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs text-indigo-600 underline"
                                      title="Open in Google Maps"
                                    >
                                      Maps
                                    </a>
                                  ) : (
                                    <span className="text-xs text-gray-400" title="No address to map">
                                      Maps
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div> */}
                </div>
              );
            })()}
          </div>


      </div>

      {/* ✅ UPDATED: Family History (Manual) — now shows Address + phone digits-only in edit modal */}
      <div className="mb-6">
        <div className="surface-card rounded-2xl p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div>
              <h3 className="section-title mb-0 flex items-center gap-2">
                <FiUsers /> Family History (Manual)
              </h3>
              <div className="section-subtitle">This is separate from Acris Parties.</div>
            </div>

            <button
              onClick={openFamilyHistoryModal}
              className="ui-btn"
              title="Edit Family History"
            >
              Edit
            </button>
          </div>

          {hasMeaningfulFamilyHistory(data) ? (
            <div className="mt-3">
              <div className="mb-3">
                <div className="text-xs text-gray-500 mb-1">Summary</div>
                <div className="text-sm text-gray-800 whitespace-pre-line">
                  {fhSummaryParsed?.trim() ? fhSummaryParsed : "—"}
                </div>
              </div>

              <div className="border rounded bg-white overflow-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
                    <tr>
                      <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Name</th>
                      <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Relationship</th>
                      <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Phones</th>
                      <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Addresses</th>
                      <th className="px-3 py-2 text-xs font-semibold text-[var(--fg-muted)] text-left">Emails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fhRelationsParsed.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-3 text-xs text-gray-500">
                          No relations added.
                        </td>
                      </tr>
                    ) : (
                      fhRelationsParsed.map((r, idx) => {
                        const phones = (Array.isArray(r?.phones) ? r.phones : r?.phone ? [r.phone] : [])
                          .map((p) => onlyDigits(p))
                          .filter(Boolean);

                        const emails = (Array.isArray(r?.emails) ? r.emails : r?.email ? [r.email] : [])
                          .map((e) => toStr(e).trim())
                          .filter(Boolean);

                        const addresses = (Array.isArray(r?.addresses) ? r.addresses : r?.address ? [r.address] : [])
                          .map((a) => toStr(a).trim())
                          .filter(Boolean);

                        return (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{(r?.name ?? "—") || "—"}</td>
                            <td className="p-2">{(r?.relationship ?? "—") || "—"}</td>
                            <td className="p-2">{phones.join(", ") || "—"}</td>
                            <td className="p-2">{addresses.join(", ") || "—"}</td>
                            <td className="p-2">{emails.join(", ") || "—"}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-gray-600">
              No family history yet. Click <strong>Edit</strong> to add members and contacts.
            </div>
          )}
        </div>
      </div>

      {/* ===== Status & Notes section ===== */}
      <div className="mb-6">
        <div className="surface-card rounded-2xl p-4">
          <div className="flex items-start justify-between mb-3 gap-3">
            <div>
              <h3 className="section-title mb-0">Status & Notes</h3>
              <div className="section-subtitle">Write notes (HTML supported).</div>
            </div>

            <div className="flex items-center gap-2" />
          </div>

          <div className="flex gap-2 mb-3">
            {["Approved", "Rejected"].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setActiveStatus(status);
                  setFollowUpDate("");
                  setSubStatus("NA");
                  setSubStatusManuallySet(true);
                }}
                className={`ui-btn ${activeStatus === status ? "ui-btn-primary" : ""}`}
              >
                {status === "Approved" ? "Lead" : "Rejected"}
              </button>
            ))}
          </div>

          {activeStatus === "Approved" && (
            <>
              <div className="flex gap-2 mb-3">
                <div className="flex-1">
                  <label className="text-sm font-medium block mb-1">Sub-Status</label>
                  <select
                    value={subStatus}
                    onChange={(e) => onSubStatusChange(e.target.value)}
                    className="w-full border px-2 py-1 rounded text-sm"
                  >
                    {subStatusOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-1">
                  <label className="text-sm font-medium block mb-1">Label</label>
                  <select
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="w-full border px-2 py-1 rounded text-sm"
                  >
                    <option value="">Select label…</option>
                    <option value="Hot">Hot</option>
                    <option value="Warm">Warm</option>
                    <option value="Cold">Cold</option>
                  </select>
                </div>
              </div>

              {subStatus === "Follow Up" && subStatusManuallySet && showCalendar && (
                <div className="mb-3">
                  <label className="text-sm block mb-1">📅 Select Follow-up Date</label>
                  <DatePicker
                    selected={followUpDate ? new Date(followUpDate) : null}
                    onChange={(date) => handleFollowUpPick(date)}
                    dateFormat="MM/dd/yyyy"
                    className="border px-2 py-1 rounded text-sm w-full"
                    placeholderText="Select a date"
                    inline
                  />
                </div>
              )}
            </>
          )}

          <div className="text-sm text-gray-700 mb-2">
            Current Status: <span className="font-semibold">{activeStatus}</span>
          </div>

          <div className="mb-3 bg-white p-2 rounded">
            {appliedRawHtml ? (
              <div className="w-full">
                <div className="mb-2 flex items-center gap-2">
                  <button
                    onClick={() => {
                      setAppliedRawHtml("");
                      if (editorRef.current) editorRef.current.innerHTML = "";
                    }}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    Clear Raw HTML
                  </button>
                  <button
                    onClick={() => {
                      if (!appliedRawHtml) {
                        toast.error("No raw HTML to open in editor.");
                        return;
                      }
                      if (editorRef.current) editorRef.current.innerHTML = appliedRawHtml;
                      setAppliedRawHtml("");
                      toast.info("Opened raw HTML in editor for editing.");
                    }}
                    className="px-2 py-1 bg-indigo-600 text-white rounded text-sm"
                  >
                    Open in Editor
                  </button>

                  <div className="text-xs text-gray-500 ml-auto">Preview (raw HTML preserved)</div>
                </div>

                <div
                  className="raw-html-preview border rounded p-2"
                  style={{ minHeight: "200px", maxWidth: "100%", overflowX: "auto", overflowY: "auto" }}
                  dangerouslySetInnerHTML={{ __html: appliedRawHtml }}
                />
              </div>
            ) : (
              <div
                ref={editorRef}
                contentEditable
                onPaste={handleEditorPaste}
                suppressContentEditableWarning
                style={{
                  minHeight: 200,
                  border: "1px solid #e5e7eb",
                  padding: 8,
                  borderRadius: 6,
                  background: "#fff",
                  overflow: "auto",
                  maxWidth: "100%",
                }}
                className="text-sm"
                aria-label="Rich editor"
              />
            )}
          </div>

          <div className="mb-3">
            <label className="text-sm font-medium block mb-1">📤 Attach PDF (optional)</label>
            <input type="file" accept="application/pdf" onChange={handlePdfUpload} disabled={uploadingPdf} />
            {uploadedPdfUrl && (
              <div className="mt-1 text-xs text-green-600">
                ✅ Attached:{" "}
                <a
                  href={uploadedPdfUrl.startsWith("http") ? uploadedPdfUrl : `${BASE_URL}${uploadedPdfUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-blue-600"
                >
                  View PDF
                </a>
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-3 items-center">
            <button
              onClick={handleSaveStatus}
              disabled={saveLoading || (activeStatus === "Approved" && (!label || label === ""))}
              className={`bg-gradient-to-r from-green-400 to-blue-500 text-white text-sm px-3 py-1 rounded font-medium self-start hover:from-green-500 hover:to-blue-600 ${
                saveLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              💾 {saveLoading ? "Saving..." : "Save Status"}
            </button>

            <button
              onClick={() => {
                if (editorRef.current) editorRef.current.innerHTML = "";
                setAppliedRawHtml("");
                setCurrentNoteId(null);
                setUploadedPdfUrl(null);
              }}
              className="text-sm px-3 py-1 border rounded"
            >
              Clear
            </button>

            <div className="text-xs text-gray-500 self-center">
              Notes save raw HTML (if pasted) or editor HTML. You can optionally attach a PDF.
            </div>
          </div>

          <div className="overflow-auto max-h-[40vh]">
            {notes.length === 0 && <div className="text-xs text-gray-500">No notes yet</div>}
            {notes.map((n) => (
              <NoteItem
                key={n.id || n.timestamp}
                note={n}
                onEdit={loadNoteIntoEditorForEdit}
                onDelete={deleteNote}
                onUpdate={(newText) => updateNoteInline(n, newText)}
                renderNoteContent={(text) => {
                  if (!text) return { __html: "—" };
                  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(text);
                  if (looksLikeHtml) return { __html: text };
                  return { __html: escapeHtml(text).replace(/\n/g, "<br/>") };
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Paste raw HTML modal */}
      {showPasteHtml && (
        <div className="fixed inset-0 z-70 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl rounded p-4">
            <h3 className="font-semibold mb-2">Paste raw HTML here</h3>
            <textarea
              value={pasteHtmlText}
              onChange={(e) => setPasteHtmlText(e.target.value)}
              className="w-full h-64 border p-2 mb-3"
              placeholder="Paste full HTML block (including inline styles/classes)"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowPasteHtml(false);
                  setPasteHtmlText("");
                }}
                className="px-3 py-1 border rounded"
              >
                Cancel
              </button>
              <button onClick={applyPastedHtml} className="px-3 py-1 bg-indigo-600 text-white rounded">
                Apply HTML (preserve styles)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Family History Modal (LegacyHome-style UI) */}
      <FamilyHistoryModal
        open={fhModalOpen}
        onClose={() => setFhModalOpen(false)}
        onSave={saveFamilyHistoryManual}
        saving={fhSaving}
        fullName={data?.full_name || ""}
        summary={fhSummary}
        setSummary={setFhSummary}
        members={fhMembers}
        setMembers={setFhMembers}
      />

      {/* HTML Viewer modal */}
      <PortalModal open={htmlModalOpen} title={"Note — Full HTML"} onClose={() => setHtmlModalOpen(false)}>
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForView(htmlModalContent) }} />
      </PortalModal>
    </div>
  );
}
